/**
 * Annotation segment-index allocation (#722 — data-loss collision).
 *
 * A new annotation's segment index must be derived from the AUTHORITATIVE set of
 * indices already in use, not from a stale `savedAnnotations` snapshot (the
 * annotations query refetch is disabled, #241). Picking an in-use index made
 * the backend overwrite the colliding annotation — silent data loss.
 */

import { ORGAN_SEGMENT_INDEX } from "./segmentationConstants";

/** Lowest free annotation segment index given the set already in use (max + 1). */
export function nextAnnotationSegmentIndex(usedSegmentIndices: number[]): number {
  return usedSegmentIndices.length > 0 ? Math.max(...usedSegmentIndices) + 1 : 1;
}

/** One viewer case owns one map. Backend labels never participate in allocation. */
export function createAnnotationLocalIndexMap() {
  const annotationLocalIndices = new Map<string, number>();
  const reservedLocalIndices = new Set<number>([ORGAN_SEGMENT_INDEX]);

  function reserve(indices: readonly number[]) {
    for (const index of indices) {
      if (!Number.isSafeInteger(index) || index <= 0) {
        throw new Error("Invalid viewer segment index");
      }
      reservedLocalIndices.add(index);
    }
  }

  function next() {
    const index = nextAnnotationSegmentIndex(
      [...reservedLocalIndices].filter((value) => value !== ORGAN_SEGMENT_INDEX),
    );
    return index === ORGAN_SEGMENT_INDEX ? index + 1 : index;
  }

  function claim() {
    const index = next();
    reserve([index]);
    return index;
  }

  function bind(annotationId: string, localIndex: number) {
    if (!annotationId) throw new Error("Missing saved annotation identity");
    const existing = annotationLocalIndices.get(annotationId);
    if (existing !== undefined && existing !== localIndex) {
      throw new Error("Saved annotation has conflicting viewer segment indices");
    }
    for (const [id, index] of annotationLocalIndices) {
      if (id !== annotationId && index === localIndex) {
        throw new Error("Viewer segment index already belongs to another annotation");
      }
    }
    reserve([localIndex]);
    annotationLocalIndices.set(annotationId, localIndex);
  }

  function ensure(annotationId: string, isOrgan = false) {
    const existing = annotationLocalIndices.get(annotationId);
    if (existing !== undefined) return existing;
    const index = isOrgan ? ORGAN_SEGMENT_INDEX : claim();
    bind(annotationId, index);
    return index;
  }

  function resolve(annotationId: string) {
    const index = annotationLocalIndices.get(annotationId);
    if (index === undefined) throw new Error("Saved annotation has no viewer segment index");
    return index;
  }

  return { reserve, next, claim, bind, ensure, resolve };
}

/**
 * Collect the annotation (non-organ) segment indices in use from a set of
 * Cornerstone contour annotations — the live, on-canvas truth (loaded-from-mask
 * contours + anything drawn this session).
 */
export function collectAnnotationSegmentIndices(annotations: unknown[]): number[] {
  const set = new Set<number>();
  for (const a of annotations ?? []) {
    const ann = a as {
      metadata?: { toolName?: string };
      data?: { segmentation?: { segmentIndex?: unknown } };
    };
    if (ann?.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") continue;
    const idx = ann?.data?.segmentation?.segmentIndex;
    if (typeof idx === "number" && idx !== ORGAN_SEGMENT_INDEX) set.add(idx);
  }
  return [...set];
}
